import React from 'react';
import { observer, inject } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import ProductAvailabilityText from '../ProductAvailabilityText';
import { AVAILABILITY_TAB_ID, SHIPPING_TAB_ID } from '../ProductPageContent';
import { modelOf } from '../../../prop-types';
import ConfigStore from '../../../store/ConfigStore';
import RequestState from '../../../types/RequestState';
import ButtonLink from '../../common/ButtonLink';
import ProductAvailabilityShipping from '../ProductAvailabilityShipping';
import FullProduct from '../../../models/product/FullProduct';

const ProductAvailabilityList = ({
  scrollToTab,
  configStore,
  product,
  activeProduct,
  shipping,
  onlineAvailability,
  storeAvailability,
  onBeforeScroll,
}) => {
  const handleScrollToTab = (tabId) => {
    if (scrollToTab) {
      scrollToTab(tabId, true);
    }
  };

  const scrollToAvailability = (e) => {
    e.stopPropagation();
    onBeforeScroll(AVAILABILITY_TAB_ID);
    handleScrollToTab(AVAILABILITY_TAB_ID);
  };

  const scrollToShipping = (e) => {
    e.stopPropagation();
    onBeforeScroll(SHIPPING_TAB_ID);
    handleScrollToTab(SHIPPING_TAB_ID);
  };

  const getStoreAvailability = () => {
    if (!configStore.product.showStoreAvailability) {
      return null;
    }

    let availabilityText;
    switch (product.stockStates.get(activeProduct.id)) {
      case RequestState.LOADING:
        availabilityText = (
          <FormattedMessage
            id="stock.loadingStoreAvailability"
            defaultMessage="Loading store availability..."
          />
        );
        break;
      case RequestState.LOADED:
        const stocks = product.stocks.get(activeProduct.id);
        const hasMultipleStores = stocks.length > 1;
        const available = stocks.some((stock) => stock.free_quantity > 0);
        if (available) {
          const availabilityMessage = (
            <ButtonLink
              className="ProductAvailabilityList__store-availability-message"
              onClick={scrollToAvailability}
            >
              <FormattedMessage
                id="stock.availableInStores"
                defaultMessage="Check store availability here"
              />
            </ButtonLink>
          );
          availabilityText = (
            <span className="ProductAvailabilityList__store-availability-text">
              {availabilityMessage}
            </span>
          );
        } else {
          const availabilityMessage = hasMultipleStores ? (
            <FormattedMessage
              id="stock.storesOutOfStock"
              defaultMessage="Stores out of stock"
            />
          ) : (
            <FormattedMessage
              id="stock.storeOutOfStock"
              defaultMessage="Store out of stock"
            />
          );
          availabilityText = <span>{availabilityMessage}</span>;
        }
        break;
      case RequestState.ERROR:
        break;
      case RequestState.NONE:
      default:
        availabilityText = '';
        break;
    }

    return (
      <li className="ProductAvailabilityList__store-availability">
        {availabilityText}
      </li>
    );
  };

  const onlyInShop = activeProduct.availabilityOnlyInShop;

  const shouldShowShipping = configStore.product.showShippingDetails;

  return (
    <ul className="ProductAvailabilityList">
      {onlineAvailability && activeProduct.availability_html && (
        <li className="ProductAvailabilityList__availability">
          <ProductAvailabilityText
            availabilityHtml={activeProduct.availability_html}
          />
        </li>
      )}
      {storeAvailability && getStoreAvailability()}
      {!onlyInShop && shipping && shouldShowShipping && (
        <ProductAvailabilityShipping
          activeProductId={activeProduct.id}
          product={product}
          scrollToShipping={scrollToShipping}
        />
      )}
    </ul>
  );
};

ProductAvailabilityList.propTypes = {
  scrollToTab: PropTypes.func.isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  product: modelOf(FullProduct).isRequired,
  activeProduct: PropTypes.shape({
    id: PropTypes.string.isRequired,
    availability_html: PropTypes.string,
  }).isRequired,
  shipping: PropTypes.bool,
  onlineAvailability: PropTypes.bool,
  storeAvailability: PropTypes.bool,
  onBeforeScroll: PropTypes.func,
};

ProductAvailabilityList.defaultProps = {
  onlineAvailability: true,
  storeAvailability: true,
  onBeforeScroll: () => {},
};

export default inject('configStore')(observer(ProductAvailabilityList));
